import { ThemeColor } from '../types/template';
import { seasonalThemes, themeColors } from '../types/template';

const THEME_SETTINGS_KEY = 'character-creator-theme';

interface ThemeSettings {
  theme: ThemeColor;
  seasonalTheme: keyof typeof seasonalThemes | null;
  darkMode: boolean;
  useCustomColors: boolean;
  customColors?: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

const defaultSettings: ThemeSettings = {
  theme: 'purple',
  seasonalTheme: null,
  darkMode: false,
  useCustomColors: false
};

export function getThemeSettings(): ThemeSettings {
  try {
    const saved = localStorage.getItem(THEME_SETTINGS_KEY);
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  } catch {
    return defaultSettings;
  }
}

export function saveThemeSettings(settings: Partial<ThemeSettings>): void {
  const current = getThemeSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(THEME_SETTINGS_KEY, JSON.stringify(updated));
}

export function getCurrentSeasonalTheme(): keyof typeof seasonalThemes | null {
  const settings = getThemeSettings();
  if (settings.seasonalTheme) return settings.seasonalTheme;

  const now = new Date();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const currentDate = `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

  for (const [season, theme] of Object.entries(seasonalThemes)) {
    const start = theme.startDate;
    const end = theme.endDate;

    if (start > end) {
      if (currentDate >= start || currentDate <= end) {
        return season as keyof typeof seasonalThemes;
      }
    } else {
      if (currentDate >= start && currentDate <= end) {
        return season as keyof typeof seasonalThemes;
      }
    }
  }

  return null;
}

export function getThemeColors(settings?: ThemeSettings): {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
} {
  const currentSettings = settings || getThemeSettings();

  if (currentSettings.useCustomColors && currentSettings.customColors) {
    const { primary, secondary, accent } = currentSettings.customColors;
    return {
      primary,
      secondary,
      accent,
      background: `bg-gradient-to-br from-[${primary}] to-[${secondary}]`,
      text: currentSettings.darkMode ? 'text-white' : 'text-gray-900'
    };
  }

  const seasonalTheme = getCurrentSeasonalTheme();
  if (seasonalTheme && seasonalThemes[seasonalTheme]) {
    const colors = seasonalThemes[seasonalTheme].colors;
    return {
      ...colors,
      text: currentSettings.darkMode ? 'text-white' : 'text-gray-900'
    };
  }

  return {
    primary: '#8B5CF6',
    secondary: '#6366F1',
    accent: '#EC4899',
    background: currentSettings.darkMode
      ? 'bg-gradient-to-br from-gray-900 to-gray-800'
      : 'bg-gradient-to-br from-purple-600 to-indigo-600',
    text: currentSettings.darkMode ? 'text-white' : 'text-gray-900'
  };
}

export function applyTheme(settings?: ThemeSettings): void {
  const currentSettings = settings || getThemeSettings();
  const colors = getThemeColors(currentSettings);

  document.documentElement.style.setProperty('--theme-primary', colors.primary);
  document.documentElement.style.setProperty('--theme-secondary', colors.secondary);
  document.documentElement.style.setProperty('--theme-accent', colors.accent);

  if (currentSettings.darkMode) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}